import { PureComponent } from 'react';
import { ButtonPrimary } from '@spotify-internal/encore-web';
import PropTypes from 'prop-types';

import { withUserInfo } from 'context/UserInfo/UserInfoProvider';
import { withMarketInfo } from 'context/MarketInfo/MarketInfoProvider';
import withTranslation from 'lib/shared/i18n/withTranslation';
import EmailField from 'components/Fields/EmailField/EmailField';
import TextField from 'components/Fields/TextField/TextField';
import TextareaField from 'components/Fields/TextareaField/TextareaField';
import {
  CLAIMANT_DETAILS,
  RUSSIAN_MARKET_LIST,
} from 'components/ContentReporting/ContentLegalReporting/content-legal-reporting-config';
import analytics, {
  ShowStepEventLabel,
} from 'components/ContentReporting/ContentLegalReporting/report-content-analytics-utils';
import {
  getFieldValid,
  getFieldValue,
  getRequiredFields,
  filterForMarket,
} from 'components/ContentReporting/form-utils';

const TEXT_FIELDS_CONFIG = [
  {
    fieldName: CLAIMANT_DETAILS.fields.CLAIMANT_FIRST_NAME,
    fieldLabel: CLAIMANT_DETAILS.labels.CLAIMANT_FIRST_NAME,
    required: true,
    excluded_markets: RUSSIAN_MARKET_LIST,
    maxLength: 40,
  },
  {
    fieldName: CLAIMANT_DETAILS.fields.CLAIMANT_FIRST_NAME,
    fieldLabel: CLAIMANT_DETAILS.labels.CLAIMANT_FIRST_NAME_PATRONYMIC,
    required: true,
    markets: RUSSIAN_MARKET_LIST,
    maxLength: 40,
  },
  {
    fieldName: CLAIMANT_DETAILS.fields.CLAIMANT_LAST_NAME,
    fieldLabel: CLAIMANT_DETAILS.labels.CLAIMANT_LAST_NAME,
    required: true,
    maxLength: 80,
  },
  {
    fieldName: CLAIMANT_DETAILS.fields.CLAIMANT_PASSPORT_NUMBER,
    fieldLabel: CLAIMANT_DETAILS.labels.CLAIMANT_PASSPORT_NUMBER,
    required: true,
    markets: RUSSIAN_MARKET_LIST,
    maxLength: 30,
  },
  {
    fieldName: CLAIMANT_DETAILS.fields.CLAIMANT_PASSPORT_AUTHORITY,
    fieldLabel: CLAIMANT_DETAILS.labels.CLAIMANT_PASSPORT_AUTHORITY,
    required: true,
    markets: RUSSIAN_MARKET_LIST,
    maxLength: 100,
  },
  {
    fieldName: CLAIMANT_DETAILS.fields.CLAIMANT_PASSPORT_ISSUED_DATE,
    fieldLabel: CLAIMANT_DETAILS.labels.CLAIMANT_PASSPORT_ISSUED_DATE,
    required: true,
    markets: RUSSIAN_MARKET_LIST,
    maxLength: 20,
  },
  {
    fieldName: CLAIMANT_DETAILS.fields.CLAIMANT_COMPANY_NAME,
    fieldLabel: CLAIMANT_DETAILS.labels.CLAIMANT_COMPANY_NAME,
    required: false,
    maxLength: 80,
  },
  {
    fieldName: CLAIMANT_DETAILS.fields.CLAIMANT_ADDRESS,
    fieldLabel: CLAIMANT_DETAILS.labels.CLAIMANT_ADDRESS,
    required: true,
    markets: RUSSIAN_MARKET_LIST,
    maxLength: 255,
  },
  {
    fieldName: CLAIMANT_DETAILS.fields.CLAIMANT_ADDRESS,
    fieldLabel: CLAIMANT_DETAILS.labels.CLAIMANT_ADDRESS,
    required: false,
    excluded_markets: RUSSIAN_MARKET_LIST,
    maxLength: 255,
  },
  {
    fieldName: CLAIMANT_DETAILS.fields.CLAIMANT_PHONE,
    fieldLabel: CLAIMANT_DETAILS.labels.CLAIMANT_PHONE,
    required: true,
    markets: RUSSIAN_MARKET_LIST,
    maxLength: 40,
  },
  {
    fieldName: CLAIMANT_DETAILS.fields.CLAIMANT_PHONE,
    fieldLabel: CLAIMANT_DETAILS.labels.CLAIMANT_PHONE,
    required: false,
    excluded_markets: RUSSIAN_MARKET_LIST,
    maxLength: 40,
  },
];

// The payload functions will be used to render in the future so we need to make sure this is kept in sync
export const getPayloadFields = (fieldValue, market) => {
  const fields = TEXT_FIELDS_CONFIG.filter(fieldConfig => filterForMarket(fieldConfig, market)).map(
    field => field.fieldName,
  );

  fields.push(CLAIMANT_DETAILS.fields.CLAIMANT_EMAIL, CLAIMANT_DETAILS.fields.CLAIMANT_MORE_INFO);
  return fields;
};

const renderTextFields = (handleFieldChange, forceValidation, fieldValue, fieldValid, currentMarket, t) => (
  <>
    {TEXT_FIELDS_CONFIG.filter(fieldConfig => filterForMarket(fieldConfig, currentMarket)).map(
      ({ fieldName, fieldLabel, required, maxLength }) => (
        <TextField
          field={{
            name: fieldName,
            type: 'text',
            required: required,
            maxlength: maxLength,
            labelOverride: t(fieldLabel),
          }}
          value={fieldValue(fieldName)}
          valid={fieldValid(fieldName)}
          onFieldChange={handleFieldChange}
          forceValidation={forceValidation}
          key={fieldName}
          errorMessageKey="infringement_claim_form:field_required"
        />
      ),
    )}
  </>
);

class Step1YourDetails extends PureComponent {
  static propTypes = {
    formData: PropTypes.object.isRequired,
    handleFieldChange: PropTypes.func.isRequired,
    renderErrorMessage: PropTypes.func.isRequired,
    forceValidation: PropTypes.bool.isRequired,
    goToNextStep: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    market: PropTypes.string.isRequired,
    onEmailFieldChange: PropTypes.func.isRequired,
  };

  componentDidMount() {
    analytics.trackShowStepEvent(ShowStepEventLabel.Step1ClaimantInformation);
    window.scrollTo(0, 0);
  }

  render() {
    const {
      formData,
      handleFieldChange,
      renderErrorMessage,
      forceValidation,
      goToNextStep,
      t,
      market,
      onEmailFieldChange,
    } = this.props;

    const fieldValue = getFieldValue(formData);
    const fieldValid = getFieldValid(formData);
    const requiredFields = [...getRequiredFields(TEXT_FIELDS_CONFIG, market), CLAIMANT_DETAILS.fields.CLAIMANT_EMAIL];

    return (
      <>
        {renderTextFields(handleFieldChange, forceValidation, fieldValue, fieldValid, market, t)}
        <EmailField
          field={{
            name: CLAIMANT_DETAILS.fields.CLAIMANT_EMAIL,
            type: 'email',
            required: true,
            maxlength: 80,
            labelOverride: t(CLAIMANT_DETAILS.labels.CLAIMANT_EMAIL),
          }}
          value={fieldValue(CLAIMANT_DETAILS.fields.CLAIMANT_EMAIL)}
          valid={fieldValid(CLAIMANT_DETAILS.fields.CLAIMANT_EMAIL)}
          errorMessageKey="infringement_claim_form:email_invalid"
          onFieldChange={onEmailFieldChange}
          forceValidation={forceValidation}
          validateOnChange
        />
        <TextareaField
          field={{
            name: CLAIMANT_DETAILS.fields.CLAIMANT_MORE_INFO,
            type: 'text',
            maxlength: 200,
            labelOverride: t(CLAIMANT_DETAILS.labels.CLAIMANT_MORE_INFO),
          }}
          value={fieldValue(CLAIMANT_DETAILS.fields.CLAIMANT_MORE_INFO)}
          onFieldChange={handleFieldChange}
        />
        {renderErrorMessage()}
        <ButtonPrimary onClick={() => goToNextStep(requiredFields)}>{t('continue')}</ButtonPrimary>
      </>
    );
  }
}

export default withTranslation('infringement_claim_form')(withUserInfo(withMarketInfo(Step1YourDetails)));
